import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import { alpha, Button, Container, useMediaQuery } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

/** Redux */
import { useDispatch, useSelector } from "react-redux";
import { getShowIDs } from "../../../_appStore/Common";
import { getTableOrChart } from "../../../_appStore/DB_Common";
import { setDatesSelected } from "../../../_appStore/DB_Dates";
import {
    clear,
    getMultiSections,
    setCurrentSection,
    setDefaults,
    setMultiSections,
    setExpandedPositions,
    setFiltersChanged,
} from "../../../_appStore/DB_Filters_Table";
import {
    getCurrentIndicator,
    getSelectedIndicators,
    setCurrentIndicator,
    setSelectedIndicators,
} from "../../../_appStore/DB_Indicators";
import { setSelectedTerritorialUnitsItem } from "../../../_appStore/DB_JTs";

/** Helpers & UI */
import { Icon } from "@/src/components/Icons";
import useHelper from "../../../_common/useHelper";
const AppTooltip = dynamic(() => import("../../../_common/AppTooltip"));
const AppMessageBox = dynamic(() => import("../../../_common/AppMessageBox"));

const useStyles = makeStyles((theme) => ({
    IndicatorsChipsHolder: {
        padding: 0,
        width: "100%",
        // width: "calc(100% - 120px)",
        alignItems: "flex-start",
        justifyContent: "flex-start", // "space-between",
        display: "flex",
        flexWrap: "wrap",
        marginLeft: theme.spacing(2.5),
        marginBottom: theme.spacing(1),
        [theme.breakpoints.down("md")]: {
            marginLeft: 0,
            marginBottom: 0,
        },
        [theme.breakpoints.down("sm")]: {
            marginLeft: 0,
            marginBottom: 0,
        },
    },
    btnWSK: {
        border: `1px solid ${alpha(theme.palette.primary.main, 1)}`,
        fontSize: "0.88888889rem",
        fontWeight: 400,
        letterSpacing: 1.5,
        textTransform: "none",
        color: theme.palette.primary.main,
        maxHeight: 51,
        width: "18%",
        "@media (any-pointer: coarse)": {
            width: "unset",
        },

        padding: theme.spacing(1.25, 2.5),
        justifyContent: "space-between",
        marginBottom: theme.spacing(1.25),
        marginTop: theme.spacing(1.255),
        marginRight: 28,
        "& + &": {
            // marginRight: 28,
            // [theme.breakpoints.down("lg")]: {
            "@media (max-width: 1365px)": {
                marginLeft: 12,
            },
            [theme.breakpoints.down("md")]: {
                marginLeft: 0,
            },
            [theme.breakpoints.down("sm")]: {
                marginLeft: 0,
            },
        },
        // [theme.breakpoints.down("lg")]: {
        "@media (max-width: 1365px)": {
            // width: "49%",
            width: "calc(33% - 12px)",
            maxHeight: "unset",
            height: "unset",
            margin: theme.spacing(0.5, 0, 1, 0),
            padding: theme.spacing(1, 2),
            // textAlign: "left",
        },
        [theme.breakpoints.down("md")]: {
            // width: "100%",
            width: "calc(100% - 8px)",
            // maxHeight: "unset",
            // height: "unset",
            margin: theme.spacing(0.5, 0, 1, 0),
            padding: theme.spacing(1, 2),
            textAlign: "left",
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            maxHeight: "unset",
            height: "unset",
            margin: theme.spacing(0.5, 0, 1, 0),
            padding: theme.spacing(1, 2),
            textAlign: "left",
        },
    },
    btnWSKActive: {
        fontWeight: 500,
        backgroundColor: `${theme.palette.primary.colors["50"]}!important`,
        border: `1px solid transparent`,
    },
    removeIconWrap: {
        display: "flex",
        alignItems: "center",
    },
    labelText: {
        fontWeight: "inherit",
        whiteSpace: "nowrap",
        maxWidth: "90%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "block",
        // "@media (max-width: 1365px)": {
        "@media (any-pointer: coarse)": {
            whiteSpace: "wrap",
        },
        [theme.breakpoints.down("lg")]: {
            whiteSpace: "wrap",
        },
        [theme.breakpoints.down("md")]: {
            whiteSpace: "wrap",
        },
        [theme.breakpoints.down("sm")]: {
            whiteSpace: "wrap",
        },
    },
}));

const IndicatorsChips = () => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { translationFromJson } = useHelper();
    const dispatch = useDispatch();
    const indicatorsRefs = useRef([]);

    const isTouch = useMediaQuery("(any-pointer: coarse)");
    const isTablet = useMediaQuery("(max-width: 1365px)");

    /** Components states */
    const [SELECTED_INDICATORS, setSELECTED_INDICATORS] = useState([]);
    const [ACTIVE, setACTIVE] = useState(null);
    const [ALERT, setALERT] = useState({ open: false });

    /** Redux states */
    const currentIndicator = useSelector(getCurrentIndicator);
    const multiSections = useSelector(getMultiSections);
    const selectedIndicators = useSelector(getSelectedIndicators);
    const showIDs = useSelector(getShowIDs);

    useEffect(() => {
        // setSELECTED_INDICATORS(selectedIndicators);

        let _newSelectedIndicators = JSON.parse(JSON.stringify(selectedIndicators));
        if (i18n.language === "pl") {
            _newSelectedIndicators = _newSelectedIndicators?.sort((a, b) => a?.name?.localeCompare(b?.name));
        }
        if (i18n.language === "en") {
            _newSelectedIndicators = _newSelectedIndicators?.sort((a, b) => a?.name_en?.localeCompare(b?.name_en));
        }

        setSELECTED_INDICATORS(_newSelectedIndicators);
    }, [selectedIndicators, i18n.language]);

    useEffect(() => {
        const activeIndicator = selectedIndicators?.map((i) => i.id).includes(currentIndicator?.id)
            ? currentIndicator
            : selectedIndicators[0];

        setACTIVE(activeIndicator);

        if (JSON.stringify(currentIndicator) !== JSON.stringify(activeIndicator)) {
            dispatch(setCurrentIndicator(activeIndicator));
        }
    }, [currentIndicator, selectedIndicators]);

    /** Handlers */
    const handleSetCurrent = ({ event, indicator }) => {
        if (event.target.closest("span")?.hasAttribute("prevent")) return;

        if (indicator && indicator.id) {
            dispatch(setExpandedPositions([]));
            dispatch(setCurrentIndicator(indicator));
        }
    };

    const handleClose = () => setALERT({ open: false });

    const handleRemove = ({ event, indicator }) => {
        event.preventDefault();
        setALERT({
            open: true,
            message: (
                <blockquote>
                    <h4>{`${translationFromJson(indicator, "name").replace(/(<([^>]+)>)/gi, "")}`}</h4>
                </blockquote>
            ),
            indicator,
        });
    };

    const handleConfirm = () => {
        const reselectedIndicators = selectedIndicators.filter(
            (s) => s.id.toString() !== ALERT.indicator.id.toString()
        );

        const _current_indicator = JSON.parse(JSON.stringify(currentIndicator));

        const activeIndicator = reselectedIndicators?.map((i) => i.id).includes(_current_indicator?.id)
            ? _current_indicator
            : reselectedIndicators[0];

        setACTIVE(activeIndicator);

        if (JSON.stringify(_current_indicator) !== JSON.stringify(activeIndicator)) {
            dispatch(setCurrentIndicator(activeIndicator));
        }

        if (!reselectedIndicators.length || ALERT.indicator.id.toString() === _current_indicator.id.toString()) {
            if (!reselectedIndicators.length) {
                dispatch(clear());
            } else {
                // dispatch(setCurrentSection(null));
                dispatch(setDefaults(null));
            }
        }
        setALERT({ open: false });

        if (reselectedIndicators.length) {
            let _newMultiSections = JSON.parse(JSON.stringify(multiSections));
            delete _newMultiSections[ALERT.indicator.indicator_id];
            dispatch(setMultiSections(_newMultiSections));
        }

        dispatch(setSelectedTerritorialUnitsItem([]));
        dispatch(setDatesSelected([]));
        dispatch(setSelectedIndicators(reselectedIndicators));
        dispatch(setFiltersChanged(true));
    };

    /** Helpers & Funcs */
    const maxChipWidth = () => {
        let width = "31%";
        switch (SELECTED_INDICATORS.length) {
            case 1:
                width = "100%";
                break;
            case 2:
                width = "49%";
                break;

            default:
                width = "calc(33% - 8px)";
                break;
        }
        return width;
    };

    return (
        <Container maxWidth={false} className={classes.IndicatorsChipsHolder}>
            {!!SELECTED_INDICATORS.length &&
                !!SELECTED_INDICATORS[0]?.id &&
                SELECTED_INDICATORS.map((indicator, i) => {
                    return (
                        <React.Fragment key={indicator.id}>
                            <AppTooltip
                                title={
                                    indicatorsRefs.current[i]?.getAttribute("show-tip") === "true"
                                        ? translationFromJson(indicator, "name").replace(/(<([^>]+)>)/gi, "")
                                        : ""
                                }
                                arrow
                                placement={"top-start"}
                                type={"dark"}
                            >
                                <Button
                                    key={indicator.id}
                                    size={"small"}
                                    style={{ maxWidth: isTablet || isTouch ? "100%" : maxChipWidth() }}
                                    className={clsx(
                                        classes.btnWSK,
                                        ACTIVE?.id?.toString() === indicator?.id?.toString() && classes.btnWSKActive
                                    )}
                                    variant={ACTIVE?.id?.toString() === indicator?.id?.toString() ? "text" : "text"}
                                    endIcon={
                                        <>
                                            <AppTooltip
                                                title={t("Usuń zmienną")}
                                                arrow
                                                placement={"right"}
                                                type={"dark"}
                                            >
                                                <span
                                                    prevent={"prevent"}
                                                    onClick={(event) =>
                                                        handleRemove({
                                                            event,
                                                            indicator,
                                                        })
                                                    }
                                                    className={classes.removeIconWrap}
                                                    aria-label={t("Usuń zmienną {{title}}", {
                                                        title: translationFromJson(indicator, "name").replace(
                                                            /(<([^>]+)>)/gi,
                                                            ""
                                                        ),
                                                    })}
                                                >
                                                    <Icon icon="carbon:close-filled" sx={{ width: 20, height: 20 }} />
                                                </span>
                                            </AppTooltip>
                                        </>
                                    }
                                    onClick={(event) => handleSetCurrent({ event, indicator })}
                                >
                                    <span
                                        className={classes.labelText}
                                        id={`overf-${i}`}
                                        ref={(ref) => {
                                            if (!ref) return;
                                            indicatorsRefs.current[i] = ref;
                                            indicatorsRefs.current[i]?.setAttribute(
                                                "show-tip",
                                                ref.scrollWidth > ref.clientWidth
                                            );
                                        }}
                                    >
                                        {showIDs ? <sup>{indicator.indicator_id}</sup> : null}
                                        {`${translationFromJson(indicator, "name").replace(/(<([^>]+)>)/gi, "")}`}
                                    </span>
                                </Button>
                            </AppTooltip>
                        </React.Fragment>
                    );
                })}
            {!!ALERT.open && (
                <AppMessageBox
                    open={ALERT.open}
                    type={"error"}
                    onClose={handleClose}
                    onRefuse={handleClose}
                    onConfirm={handleConfirm}
                    title={t("Usuwanie wybranego wskaźnika")}
                    message={ALERT.message}
                    buttons={{ OK: t("Usuń"), CANCEL: t("Anuluj") }}
                />
            )}
        </Container>
    );
};

export default IndicatorsChips;
